import { Grid, Text, Stack, Group } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Button, CopyButton } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { DateCell } from "~/components/cells/DateCell"
import {
  findCertificateRequestsForSupplierQueryKey,
  FindCertificateRequestsForSupplierResponseDto,
  useUpdateCertificateRequest,
} from "~/gen/api"
import { useCertificateRequestsBatchModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/store/CertificateRequestsBatchModalContext"
import { CertificateRequestBatchPopover } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestBatchPopover"
import { CertificateRequestProductionSite } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestProductionSite"
import { CertificateRequestPurchaseOrder } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestPurchaseOrder"
import { CertificatesSelect } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateSelect"
import { DeleteCertificateRequestModal } from "~/pages/SupplierPortal/TraceabilityRequests/components/DeleteCertificateRequestModal"
import { useAuth } from "~/store/auth-context.tsx"
import { useGetOperations } from "~/utils/operations"

interface Props {
  certificateRequest: FindCertificateRequestsForSupplierResponseDto
  setTransferCertificateRequestId: (id: string | undefined) => void
  openTransferModal: () => void
}

export const CertificateRequest = ({
  certificateRequest,
  setTransferCertificateRequestId,
  openTransferModal,
}: Props) => {
  const { isAdmin } = useAuth()

  const [certificateId, setCertificateId] = useState<string | null>(
    certificateRequest.certificateId
  )

  //Necessary for live update after query invalidation in batch update
  useEffect(() => {
    setCertificateId(certificateRequest.certificateId)
  }, [certificateRequest.certificateId])

  const { t } = useTranslation(["traceability"])
  const activity = useGetOperations()[certificateRequest.activityName]

  const { mutate: updateCertificateRequest } = useUpdateCertificateRequest(
    certificateRequest.id,
    {
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: findCertificateRequestsForSupplierQueryKey(),
          })
        },
      },
    }
  )

  const handleSubmit = () => {
    updateCertificateRequest({
      status: "CLOSED",
    })
  }

  const handleUpdate = (newCertificateId: string | null) => {
    updateCertificateRequest({
      certificateId: newCertificateId,
    })
  }

  const [
    openedDeleteModal,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const productionSiteId = certificateRequest.productionSite?.id

  const [opened, { close: closePopover, open: openPopover }] =
    useDisclosure(false)

  const { openBatchModal } = useCertificateRequestsBatchModalContext()

  return (
    <Grid>
      <Grid.Col span={4}>
        <Stack gap="sm">
          {isAdmin && (
            <CopyButton
              label="CertificateRequest Id"
              size="xs"
              copyableText={certificateRequest.id}
              admin={isAdmin}
            />
          )}
          <Text fw={700} fz="sm">
            {certificateRequest.type.name}
          </Text>
          <CertificateRequestProductionSite
            productionSite={certificateRequest.productionSite}
          />
          {certificateRequest.type.category === "TRANSACTION" && (
            <CertificateRequestPurchaseOrder
              certificateRequest={certificateRequest}
            />
          )}
          <Group>
            <Text size="sm">{t("certificates.activityName")}</Text>
            <Group gap="xs">
              {activity.icon && <activity.icon size={20} />}
              <Text size="sm">{activity.name}</Text>
            </Group>
          </Group>
          <Group>
            <Text fw={700} fz="sm">
              {t("certificates.labels.maxIssueDate")}
            </Text>
            <DateCell date={certificateRequest.maxIssueDate} />
          </Group>
        </Stack>
      </Grid.Col>
      <Grid.Col span={6}>
        <Stack gap="sm">
          <Stack>
            <CertificateRequestBatchPopover
              target={
                <Stack>
                  <CertificatesSelect
                    certificateId={certificateId}
                    productionSiteId={productionSiteId}
                    onCertificateSelected={(certificateId) => {
                      setCertificateId(certificateId)
                      handleUpdate(certificateId)
                      if (
                        certificateId &&
                        certificateRequest.type.category === "TRANSACTION"
                      ) {
                        openPopover()
                      }
                    }}
                    typeId={certificateRequest.type.id}
                    disabled={certificateRequest.status === "CLOSED"}
                  />
                </Stack>
              }
              onClick={() => {
                if (certificateId) {
                  closePopover()
                  openBatchModal({
                    certificateId,
                    certificateRequestId: certificateRequest.id,
                  })
                }
              }}
              opened={opened}
              close={closePopover}
            />
          </Stack>
        </Stack>
      </Grid.Col>
      <Grid.Col span={2}>
        {certificateRequest.status === "OPENED" && (
          <Stack>
            <Button disabled={!certificateId} onClick={handleSubmit}>
              {t("certificates.actions.submit")}
            </Button>
            <Button
              onClick={() => {
                setTransferCertificateRequestId(certificateRequest.id)
                openTransferModal()
              }}
            >
              {t("certificates.actions.transfer")}
            </Button>
            {isAdmin && (
              <>
                <Button
                  color="violet"
                  variant="secondary"
                  onClick={() => {
                    openDeleteModal()
                  }}
                >
                  {t("certificates.actions.delete")}
                </Button>
                {isAdmin && (
                  <DeleteCertificateRequestModal
                    opened={openedDeleteModal}
                    close={closeDeleteModal}
                    certificateRequest={certificateRequest}
                  />
                )}
              </>
            )}
          </Stack>
        )}
      </Grid.Col>
    </Grid>
  )
}
